// ============= Imports =============
import { AuthContextProps } from '@/contexts/AuthContext';
import { tokenManager } from '@tw/tw-component-library';

// ============= Constants =============
const BASE_URL = import.meta.env.VITE_BASE_CORE_URL;
const API_ENDPOINT = '/api/Workspace';
const LITIUM_SCOPE = import.meta.env.VITE_LITIUM_CORE_SCOPE;

const HEADERS = {
  'Content-Type': 'application/json',
};

// ============= Types =============

export interface PatchUpdatesBasicWorkspaceDetailsBody {
  title?: string;
  description?: string;
  clientUno?: string | null;
  matterUno?: string | null;
}

export interface PatchUpdatesBasicWorkspaceDetailsProps {
  getAccessToken: AuthContextProps['getAccessToken'];
  workspaceId: string;
  body?: PatchUpdatesBasicWorkspaceDetailsBody;
}

// ============= Utility Functions =============
/**
 * Creates headers for API request
 * @param token Authentication token
 * @returns Headers object
 */
const createHeaders = (token: string) => ({
  Authorization: `Bearer ${token}`,
  ...HEADERS,
});

// ============= Main Function =============
/**
 * Updates basic workspace details
 * @param getAccessToken Function to acquire access token
 * @param body Optional request body
 * @returns Promise with API response or error
 */
export const patchUpdatesBasicWorkspaceDetails = async ({
  getAccessToken,
  workspaceId,
  body,
}: PatchUpdatesBasicWorkspaceDetailsProps): Promise<any> => {
  try {
    console.log('🔄 Updating workspace details...');

    // Token acquisition with required scopes
    const token = await getAccessToken([LITIUM_SCOPE]);
    if (!token) {
      throw new Error('Failed to acquire API token');
    }
    tokenManager.logTokenInfo(token, 'API');

    // URL construction
    const url = `${BASE_URL}${API_ENDPOINT}/patch/${workspaceId}`;
    const convertURI = encodeURI(url);
    console.log('🌐 Making request to:', convertURI);

    // API request
    const response = await fetch(convertURI, {
      method: 'PATCH',
      headers: createHeaders(token),
      body: body ? JSON.stringify(body) : undefined,
    });

    // Response handling
    if (!response.ok) {
      console.log(`HTTP error! status: ${response.status}`);
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data = await response.json();
    console.log(`✅ Workspace details updated successfully: ${data}`);

    return data;
  } catch (error) {
    console.error('🔴 Error updating workspace details:', error);
    throw error;
  }
};
